import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EventNoteRoundedIcon from "@mui/icons-material/EventNoteRounded";

import Langs, { Localize } from "../../app/lang/langs";
import { confirmDialog } from "../../components/popups/ConfirmDialog";
import { formatDate } from "../../app/utils/helpers";
import {
    getLeaveAdjustments,
    addLeaveAdjustment,
    removeLeaveAdjustment,
} from "./leavesSlice";

const ORANGE = "#FF9015";

// Scoped theme so MUI controls pick up the Vital brand accent instead of the
// default blue. There is no global MUI theme in the app.
const brandTheme = createTheme({
    palette: {
        primary: { main: ORANGE, contrastText: "#ffffff" },
        error: { main: "#fb6962" },
        success: { main: "#3a9c60" },
    },
    shape: { borderRadius: 8 },
    typography: { fontFamily: "roboto, sans-serif" },
});

const ADJUSTMENT_TYPES = ["CP", "SICK", "EXTRA"];
const TYPE_COLORS = { CP: "primary", SICK: "error", EXTRA: "success" };

const buildYearOptions = () => {
    const current = new Date().getFullYear();
    const years = [];
    for (let y = current + 1; y >= current - 3; y--) years.push(y);
    return years;
};

const FIELD_HEIGHT = 40;

export function LeaveAdjustmentsModal({ show, onHide }) {
    const dispatch = useDispatch();

    const collabs = useSelector((state) => state.collabs.currentList);
    const adjustments = useSelector((state) => state.leaves.adjustments);
    const adjustmentsLoaded = useSelector((state) => state.leaves.adjustmentsLoaded);

    const yearOptions = useMemo(() => buildYearOptions(), []);

    const [selectedCollab, setSelectedCollab] = useState(null);
    const [year, setYear] = useState(new Date().getFullYear());
    const [leaveType, setLeaveType] = useState("CP");
    const [days, setDays] = useState("");
    const [reason, setReason] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const fetchAdjustments = (idCollab, leaveYear) => {
        if (idCollab && leaveYear) {
            dispatch(getLeaveAdjustments({ idCollab, leaveYear }));
        }
    };

    useEffect(() => {
        if (show && selectedCollab) {
            fetchAdjustments(selectedCollab.id, year);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, selectedCollab, year]);

    // Reset the form whenever the modal is closed.
    useEffect(() => {
        if (!show) {
            setSelectedCollab(null);
            setYear(new Date().getFullYear());
            setLeaveType("CP");
            setDays("");
            setReason("");
        }
    }, [show]);

    const numDays = parseFloat(days);
    const canSubmit =
        selectedCollab && year && leaveType && !isNaN(numDays) && numDays !== 0 && !submitting;

    const handleAdd = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        await dispatch(
            addLeaveAdjustment({
                id_collab: selectedCollab.id,
                leave_year: year,
                leave_type: leaveType,
                days: numDays,
                reason: reason || null,
            })
        );
        setSubmitting(false);
        setDays("");
        setReason("");
        fetchAdjustments(selectedCollab.id, year);
    };

    const confirmRemove = (id) => {
        confirmDialog({
            title: <Langs str="LEAVE_ADJUSTMENT_DELETE_TITLE" />,
            message: <Langs str="LEAVE_ADJUSTMENT_DELETE_MSG" />,
            danger: true,
            onConfirm: () => handleRemove(id),
        });
    };

    const handleRemove = async (id) => {
        await dispatch(removeLeaveAdjustment({ id }));
        if (selectedCollab) fetchAdjustments(selectedCollab.id, year);
    };

    return (
        <ThemeProvider theme={brandTheme}>
            <Dialog
                open={!!show}
                onClose={onHide}
                maxWidth="md"
                fullWidth
                PaperProps={{ sx: { borderRadius: 2, overflow: "hidden" } }}
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        fontWeight: 700,
                        color: "#1a1a2e",
                        borderTop: `4px solid ${ORANGE}`,
                        pb: 1.5,
                    }}
                >
                    <EventNoteRoundedIcon sx={{ color: ORANGE }} />
                    <Langs str="LEAVE_ADJUSTMENTS" />
                </DialogTitle>

                <DialogContent dividers sx={{ bgcolor: "#fafbfc" }}>
                    {/* Collaborator + year selectors */}
                    <Stack
                        direction={{ xs: "column", sm: "row" }}
                        spacing={2}
                        alignItems="flex-start"
                        sx={{ mb: 3 }}
                    >
                        <Autocomplete
                            sx={{ flex: 2, width: "100%", bgcolor: "#fff" }}
                            options={collabs}
                            getOptionLabel={(option) => option.label || ""}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            value={selectedCollab}
                            onChange={(e, value) => setSelectedCollab(value)}
                            renderInput={(params) => (
                                <TextField {...params} label={Localize("COLLAB")} size="small" />
                            )}
                        />
                        <FormControl sx={{ flex: 1, minWidth: 130, bgcolor: "#fff" }} size="small">
                            <InputLabel>{Localize("YEAR")}</InputLabel>
                            <Select
                                value={year}
                                label={Localize("YEAR")}
                                onChange={(e) => setYear(e.target.value)}
                            >
                                {yearOptions.map((y) => (
                                    <MenuItem key={y} value={y}>
                                        {y}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>

                    {!selectedCollab ? (
                        <Box
                            sx={{
                                py: 5,
                                textAlign: "center",
                                color: "text.secondary",
                                border: "1px dashed #d9d9d9",
                                borderRadius: 2,
                                bgcolor: "#fff",
                            }}
                        >
                            <EventNoteRoundedIcon sx={{ fontSize: 40, color: "#c9c9c9", mb: 1 }} />
                            <Typography variant="body2">
                                <Langs str="SELECT_COLLAB_TO_ADJUST" />
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            {/* Add-adjustment form */}
                            <Paper
                                variant="outlined"
                                sx={{ p: 2, mb: 3, borderRadius: 2, bgcolor: "#fff" }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{ color: "text.secondary", fontWeight: 600 }}
                                >
                                    <Langs str="ADD" />
                                </Typography>
                                <Stack
                                    direction={{ xs: "column", md: "row" }}
                                    spacing={2}
                                    alignItems="flex-start"
                                    sx={{ mt: 1 }}
                                >
                                    <FormControl sx={{ minWidth: 150 }} size="small">
                                        <InputLabel>{Localize("TYPE")}</InputLabel>
                                        <Select
                                            value={leaveType}
                                            label={Localize("TYPE")}
                                            onChange={(e) => setLeaveType(e.target.value)}
                                            sx={{ height: FIELD_HEIGHT }}
                                        >
                                            {ADJUSTMENT_TYPES.map((t) => (
                                                <MenuItem key={t} value={t}>
                                                    {Localize(`ADJUSTMENT_TYPE_${t}`)}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        size="small"
                                        type="number"
                                        label={Localize("DAYS")}
                                        value={days}
                                        onChange={(e) => setDays(e.target.value)}
                                        sx={{ width: 130 }}
                                        helperText={Localize("ADJUSTMENT_DAYS_HINT")}
                                    />
                                    <TextField
                                        size="small"
                                        label={Localize("REASON")}
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        sx={{ flex: 1, width: "100%" }}
                                    />
                                    <Button
                                        variant="contained"
                                        disableElevation
                                        startIcon={<AddRoundedIcon />}
                                        onClick={handleAdd}
                                        disabled={!canSubmit}
                                        sx={{ height: FIELD_HEIGHT, whiteSpace: "nowrap", px: 2.5 }}
                                    >
                                        <Langs str="ADD" />
                                    </Button>
                                </Stack>
                            </Paper>

                            {/* Existing adjustments */}
                            {!adjustmentsLoaded ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                                    <CircularProgress size={28} sx={{ color: ORANGE }} />
                                </Box>
                            ) : adjustments.length === 0 ? (
                                <Typography
                                    color="text.secondary"
                                    variant="body2"
                                    sx={{ py: 3, textAlign: "center" }}
                                >
                                    <Langs str="NO_ADJUSTMENTS" />
                                </Typography>
                            ) : (
                                <TableContainer
                                    component={Paper}
                                    variant="outlined"
                                    sx={{ borderRadius: 2 }}
                                >
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f4f4f4", color: "#4b5263" } }}>
                                                <TableCell>{Localize("YEAR")}</TableCell>
                                                <TableCell>{Localize("TYPE")}</TableCell>
                                                <TableCell align="right">{Localize("DAYS")}</TableCell>
                                                <TableCell>{Localize("REASON")}</TableCell>
                                                <TableCell>{Localize("CREATED_AT")}</TableCell>
                                                <TableCell align="right" />
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {adjustments.map((adj) => (
                                                <TableRow key={adj.id} hover>
                                                    <TableCell>{adj.leave_year}</TableCell>
                                                    <TableCell>
                                                        <Chip
                                                            size="small"
                                                            variant="outlined"
                                                            color={TYPE_COLORS[adj.leave_type] || "default"}
                                                            label={Localize(`ADJUSTMENT_TYPE_${adj.leave_type}`)}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{
                                                            fontWeight: 600,
                                                            color: adj.days < 0 ? "error.main" : "success.main",
                                                        }}
                                                    >
                                                        {adj.days > 0 ? `+${adj.days}` : adj.days}
                                                    </TableCell>
                                                    <TableCell sx={{ color: adj.reason ? "inherit" : "text.disabled" }}>
                                                        {adj.reason || "—"}
                                                    </TableCell>
                                                    <TableCell sx={{ color: "text.secondary" }}>
                                                        {adj.created_at
                                                            ? formatDate(new Date(adj.created_at), "DD/MM/YYYY")
                                                            : "—"}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton
                                                            size="small"
                                                            color="error"
                                                            onClick={() => confirmRemove(adj.id)}
                                                        >
                                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                        </>
                    )}
                </DialogContent>

                <Divider />
                <DialogActions sx={{ px: 3, py: 1.5 }}>
                    <Button onClick={onHide} sx={{ color: "text.secondary" }}>
                        <Langs str="CLOSE" />
                    </Button>
                </DialogActions>
            </Dialog>
        </ThemeProvider>
    );
}

export default LeaveAdjustmentsModal;
